import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from "./context/ThemeContext";
import { AppDownloadProvider } from "./context/AppDownloadContext";
import Layout from "./components/layout/Layout";
import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import ForStudentsPage from "./pages/ForStudentsPage";
import ForInstitutionsPage from "./pages/ForInstitutionsPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import CookieSettingsPage from "./pages/CookieSettingsPage";
import AccountDeletionPage from "./pages/AccountDeletionPage";
import ChallengeLinkPage from "./pages/ChallengeLinkPage";
import InvitePage from "./pages/InvitePage";
import WaitlistPage from "./pages/WaitlistPage";
import CareersPage from "./pages/CareersPage";
import AmbassadorPage from "./pages/AmbassadorPage";
import PricingPage from "./pages/PricingPage";
import NotFoundPage from "./pages/NotFoundPage";
import SiteAnalytics from "./components/analytics/SiteAnalytics";

import "./styles/design-system.css";
import "./styles/components.css";
import "./styles/pages.css";

function ScrollToTop() {
  const { pathname } = useLocation();

  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function AppRoutes() {
  const location = useLocation();

  return (
    <Layout>
      <ScrollToTop />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<HomePage />} />
          <Route path="/product" element={<ProductPage />} />
          <Route path="/students" element={<ForStudentsPage />} />
          <Route path="/institutions" element={<ForInstitutionsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:id" element={<BlogPostPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/cookies-settings" element={<CookieSettingsPage />} />
          <Route path="/account-deletion" element={<AccountDeletionPage />} />
          <Route path="/c/:challengeCode" element={<ChallengeLinkPage />} />
          {/* Links shared by older app versions */}
          <Route path="/challenge-request/:challengeCode" element={<ChallengeLinkPage />} />
          <Route path="/invite/:code" element={<InvitePage />} />
          <Route path="/waitlist" element={<WaitlistPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/ambassador" element={<AmbassadorPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          {/* Legacy redirects */}
          <Route path="/features" element={<Navigate to="/product" replace />} />
          <Route path="/roadmap" element={<Navigate to="/product#roadmap" replace />} />
          <Route path="/for-students" element={<Navigate to="/students" replace />} />
          <Route path="/for-institutions" element={<Navigate to="/institutions" replace />} />
          <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AnimatePresence>
    </Layout>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <AppDownloadProvider>
          <AppRoutes />
          <SiteAnalytics />
        </AppDownloadProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}
